import {
  CollapsibleList,
  ListItem,
  ListItemGraphic,
  ListItemMeta,
  ListItemPrimaryText,
  ListItemSecondaryText,
  ListItemText,
  SimpleListItem
} from "@rmwc/list";
import { RESSOURCE_HISTO_ACHATS } from "Achat/API/AchatApi";
import { UNITE_FAMILLE, UNITE_TYPES } from "Achat/Constants";
import { URL_BASE } from "App/Constants";
import { t } from "i18next";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectDataReferentielUniteTypes } from "Referentiel/Store/selectors";
import { GET } from "Request/Components/RequestUtils";
import { GET_LIST_WITH_PARAMS, useJsonRequest } from "Request/Components/useJsonRequest";
import { selectDataResourceReceived } from "Request/Store/selectors";
import PropTypes from "prop-types";
import { selectIcon } from "Shared/Utils/Icon";
import { useGetServiceTypeLibelle } from "./Utils/achatHook";
import "./HistoAchats.css";

// #region SOUS-COMPOSANT LIGNE HISTORIQUE (hook de traduction par service)
function HistoAchatsListItem({ service, detailAchats, getPrix, getServiceQuantite }) {
  const translatedName = useGetServiceTypeLibelle(service.idServiceType);
  const displayName = translatedName || service.nomService;
  return (
    <ListItem
      className="HistoAchats-List-Item"
      ripple={false}
      style={{ height: "80px" }}
    >
      <ListItemGraphic icon={selectIcon(service.idServiceType)} />
      <ListItemText>
        <ListItemPrimaryText>{displayName}</ListItemPrimaryText>
        <ListItemSecondaryText>{getServiceQuantite(service)} / {service.tarifService} €</ListItemSecondaryText>
      </ListItemText>
      <ListItemMeta style={{ fontSize: 18 }} className="HistoAchats-Item-Price">
        {getPrix(service, detailAchats)} €
      </ListItemMeta>
    </ListItem>
  );
}
HistoAchatsListItem.propTypes = {
  service: PropTypes.object.isRequired,
  detailAchats: PropTypes.array.isRequired,
  getPrix: PropTypes.func.isRequired,
  getServiceQuantite: PropTypes.func.isRequired
};
// #endregion

// #region FONCTION COMPOSANT
/**
* Composant permettant d'afficher l'historique des achats d'un utilisateur
* @class
* @category Achat
*/
function HistoAchats() {
  // #region INITIALISATION
  // Initialisation des state
  const [data, setData] = useState();

  // Initialisation des sélecteurs
  const dataReceived = useSelector((state) => selectDataResourceReceived(state, RESSOURCE_HISTO_ACHATS)); // Données issues de la requête);
  const uniteTypes = useSelector((state) => selectDataReferentielUniteTypes(state));
  // #endregion

  // #region UTILS
  // Renvoie le prix
  const getPrix = (service, categorie) => {
    let result = service.quantite * service.tarifService;

    // Récupère le prix si c'est une taxe de séjour à la personne
    if (service.idUniteType === UNITE_TYPES.PERSONNE_24H) {
      const parent = categorie.find(item => item.idUniteType === UNITE_TYPES.PARKING);
      if (parent) {
        result = result * parent.quantite;
      }
    }

    return result.toFixed(2);
  };

  // Renvoie la quantité achetée avec le type d'unité
  const getServiceQuantite = (service) => {
    let uniteType = uniteTypes.data.find(type => type.idUniteType === service.idUniteType);

    switch (uniteType.famille) {
      case UNITE_FAMILLE.TEMPS:
        if (uniteType.quantite < 3600) {
          return `${(uniteType.quantite / 60) * service.quantite} ${t("achat.minutes")}`
        }

        return `${((uniteType.quantite / 60) / 60) * service.quantite} ${t("achat.hour")}`

      case UNITE_FAMILLE.VOLUME:
        return `${uniteType.quantite * service.quantite} ${uniteType.libelleType}`
      default:
        break;
    }
  };
  // #endregion

  // #region REQUEST
  // Requête de récupération des achats de l'utilisateur connecté
  const requestGetHistoAchats = useJsonRequest({
    command: GET,
    getMode: GET_LIST_WITH_PARAMS,
    resource: RESSOURCE_HISTO_ACHATS,
    url: URL_BASE,
  });
  // #endregion

  // #region HOOK D'EFFET
  // Requête des achats
  useEffect(() => {
    requestGetHistoAchats();
  }, [requestGetHistoAchats]);

  // Réception des données
  useEffect(() => {
    if (!dataReceived) {
      return;
    }

    let d = dataReceived;

    setData(d);
  }, [dataReceived]);

  // #endregion

  // #region INTERFACE
  return (
    <div className="HistoAchats-Container">
      {/* Title */}
      <h2 className="HistoAchats-Title">{t("achat.historic")}</h2>

      {/* Item */}
      {data && data.map((item, index) => (
        <CollapsibleList
          key={index}
          handle={
            <SimpleListItem
              className="HistoAchats-List-Header"
              metaIcon="chevron_right"
              // AYB 2026.02.20 affichage date système avec date et heure
              text={`${moment(item.dateSysteme).format("DD/MM/YYYY HH:mm")} - ${item.nomSite} ${item.montant ? `- ${item.montant} €` : ''}`}
              style={{ height: "60px" }}
            />
          }
        >
          {
            item.detailAchats.map((service, index) => (
              <HistoAchatsListItem
                key={index}
                service={service}
                detailAchats={item.detailAchats}
                getPrix={getPrix}
                getServiceQuantite={getServiceQuantite}
              />
            ))
          }
        </CollapsibleList>
      ))
      }
    </div >
  );
  // #endregion
};
// #endregion

export { HistoAchats };

